import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle, Circle, ChevronRight, ChevronLeft,
  Package, Building2, FileText, FileCheck, Sparkles,
  ArrowRight, Download, Check
} from "lucide-react";

// ─── Step data ────────────────────────────────────────────────────────────────

const STEPS = [
  { key: "welcome",   label: "Welcome",   icon: Sparkles },
  { key: "company",   label: "Company",   icon: Building2 },
  { key: "products",  label: "Products",  icon: Package },
  { key: "contract",  label: "Contract",  icon: FileText },
  { key: "tax",       label: "Tax Form",  icon: FileCheck },
  { key: "complete",  label: "Done!",     icon: CheckCircle },
];

const PRODUCTS = [
  {
    name: "SalesCRM Core",
    tagline: "The foundation of your pipeline",
    description: "Full CRM suite with lead management, deal tracking, and automated follow-ups. Built for speed.",
    price: "$49/mo per seat",
    highlights: ["Unlimited leads", "Visual pipeline", "Email sequences", "Mobile app"],
    color: "#6366f1",
  },
  {
    name: "AI Sales Agent",
    tagline: "Your 24/7 closer",
    description: "An AI-powered assistant that qualifies leads, drafts outreach, and suggests next steps in real time.",
    price: "$99/mo per seat",
    highlights: ["Lead scoring", "Auto-drafts", "Call summaries", "Objection handling"],
    color: "#0ea5e9",
  },
  {
    name: "Analytics Pro",
    tagline: "See the whole picture",
    description: "Deep reporting across your entire team. Forecast revenue, track conversion rates, and spot trends.",
    price: "$29/mo per seat",
    highlights: ["Revenue forecast", "Team leaderboards", "Custom dashboards", "CSV exports"],
    color: "#10b981",
  },
];

// ─── Individual Steps ─────────────────────────────────────────────────────────

function WelcomeStep({ user }) {
  return (
    <div className="onboard-step-content">
      <div className="onboard-welcome-icon">
        <Sparkles size={36} color="#6366f1" />
      </div>
      <h2 className="onboard-step-title">Welcome to the team, {user?.full_name?.split(" ")[0] ?? "there"}! 👋</h2>
      <p className="onboard-step-desc">
        We're excited to have you on board. This quick walkthrough will get you set up in about 5 minutes —
        covering the company, our products, and a couple of documents to sign.
      </p>
      <div className="onboard-checklist">
        {STEPS.slice(1, -1).map(s => (
          <div key={s.key} className="onboard-checklist-item">
            <Circle size={16} color="#94a3b8" />
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompanyStep() {
  return (
    <div className="onboard-step-content">
      <h2 className="onboard-step-title">About the Company</h2>
      <p className="onboard-step-desc">Here's what you need to know about us before your first call.</p>

      <div className="onboard-company-grid">
        <div className="onboard-company-card">
          <h4>Our Mission</h4>
          <p>Empower sales teams with tools that actually close deals — no bloat, just results.</p>
        </div>
        <div className="onboard-company-card">
          <h4>Founded</h4>
          <p>2021 · Bootstrapped · Profitable. We move fast and ship constantly.</p>
        </div>
        <div className="onboard-company-card">
          <h4>Your Role</h4>
          <p>You're an independent contractor sales rep. You own your pipeline and your commissions.</p>
        </div>
        <div className="onboard-company-card">
          <h4>Commission Structure</h4>
          <p>20% recurring on every seat you close. Paid monthly, net-15.</p>
        </div>
      </div>

      <div className="onboard-tips">
        <p className="onboard-tips-heading">Quick tips for your first week</p>
        <ul>
          <li>Reach out to your manager on Slack — they'll introduce you to current deals</li>
          <li>Review the top 3 objections in the AI Agent knowledge base</li>
          <li>Set your calendar availability for discovery calls</li>
        </ul>
      </div>
    </div>
  );
}

function ProductsStep() {
  const [selected, setSelected] = useState(null);

  return (
    <div className="onboard-step-content">
      <h2 className="onboard-step-title">Our Products</h2>
      <p className="onboard-step-desc">You'll be selling these three products. Click each to learn more.</p>

      <div className="onboard-products-grid">
        {PRODUCTS.map((p, i) => (
          <div
            key={p.name}
            className={`onboard-product-card ${selected === i ? "selected" : ""}`}
            style={{ "--product-color": p.color }}
            onClick={() => setSelected(selected === i ? null : i)}
          >
            <div className="onboard-product-top">
              <div className="onboard-product-dot" style={{ background: p.color }} />
              <div>
                <p className="onboard-product-name">{p.name}</p>
                <p className="onboard-product-tagline">{p.tagline}</p>
              </div>
              <span className="onboard-product-price">{p.price}</span>
            </div>
            {selected === i && (
              <div className="onboard-product-detail">
                <p>{p.description}</p>
                <ul className="onboard-product-highlights">
                  {p.highlights.map(h => (
                    <li key={h}><Check size={13} color={p.color} />{h}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContractStep({ onSigned, signed }) {
  const [scrolled, setScrolled] = useState(false);
  const [agreed, setAgreed] = useState(signed);
  const [signing, setSigning] = useState(false);

  async function handleSign() {
    if (!agreed) return;
    setSigning(true);
    try {
      await fetch("/api/onboarding/sign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ document: "contract" }),
      });
      onSigned("contract");
    } catch (e) {
      console.error(e);
    } finally {
      setSigning(false);
    }
  }

  return (
    <div className="onboard-step-content">
      <h2 className="onboard-step-title">Independent Contractor Agreement</h2>
      <p className="onboard-step-desc">Please read and sign your contractor agreement before proceeding.</p>

      <div
        className="onboard-doc-scroll"
        onScroll={e => {
          const el = e.target;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolled(true);
        }}
      >
        <div className="onboard-doc-content">
          <h3>Independent Contractor Agreement</h3>
          <p><strong>Effective Date:</strong> {new Date().toLocaleDateString()}</p>
          <p>
            This Independent Contractor Agreement ("Agreement") is entered into between SalesCRM Inc.
            ("Company") and the undersigned individual ("Contractor").
          </p>
          <h4>1. Services</h4>
          <p>
            Contractor agrees to provide sales representative services including prospecting, qualifying leads,
            conducting product demonstrations, and closing new customer accounts on behalf of the Company.
          </p>
          <h4>2. Compensation</h4>
          <p>
            Contractor shall receive a commission of twenty percent (20%) of the monthly recurring revenue
            generated from accounts closed by Contractor. Commissions are paid on a net-15 basis following
            each calendar month.
          </p>
          <h4>3. Independent Contractor Status</h4>
          <p>
            Contractor is an independent contractor and not an employee. Contractor is responsible for all
            applicable taxes and shall not be entitled to benefits provided to employees.
          </p>
          <h4>4. Confidentiality</h4>
          <p>
            Contractor agrees to keep confidential all proprietary information, customer data, pricing, and
            business strategies of the Company during and after the term of this Agreement.
          </p>
          <h4>5. Term and Termination</h4>
          <p>
            This Agreement commences on the Effective Date and continues until terminated by either party with
            fourteen (14) days' written notice.
          </p>
          <h4>6. Non-Solicitation</h4>
          <p>
            During the term and for twelve (12) months thereafter, Contractor shall not solicit Company
            customers or employees for competing purposes.
          </p>
          <p style={{ marginTop: 16, color: "#94a3b8", fontSize: 13 }}>
            [Scroll to the bottom to enable signing]
          </p>
        </div>
      </div>

      {signed ? (
        <div className="onboard-signed-badge">
          <CheckCircle size={18} color="#10b981" />
          <span>Contract signed</span>
        </div>
      ) : (
        <>
          <label className={`onboard-agree-check ${!scrolled ? "disabled" : ""}`}>
            <input type="checkbox" checked={agreed} disabled={!scrolled} onChange={e => setAgreed(e.target.checked)} />
            I have read and agree to the Independent Contractor Agreement
          </label>
          <button
            className="settings-btn-primary"
            disabled={!agreed || !scrolled || signing}
            onClick={handleSign}
          >
            {signing ? "Signing…" : "Sign Agreement →"}
          </button>
        </>
      )}

      <button className="settings-btn-ghost" style={{ marginTop: 8 }}>
        <Download size={14} /> Download PDF
      </button>
    </div>
  );
}

function TaxStep({ onSigned, signed }) {
  const [form, setForm] = useState({ name: "", ssn: "", address: "", city: "", state: "", zip: "" });
  const [signing, setSigning] = useState(false);

  async function handleSign(e) {
    e.preventDefault();
    setSigning(true);
    try {
      await fetch("/api/onboarding/sign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ document: "tax_form", data: form }),
      });
      onSigned("tax_form");
    } catch (err) {
      console.error(err);
    } finally {
      setSigning(false);
    }
  }

  if (signed) {
    return (
      <div className="onboard-step-content">
        <h2 className="onboard-step-title">W-9 Tax Form</h2>
        <div className="onboard-signed-badge">
          <CheckCircle size={18} color="#10b981" />
          <span>W-9 signed and submitted</span>
        </div>
        <button className="settings-btn-ghost" style={{ marginTop: 12 }}>
          <Download size={14} /> Download W-9
        </button>
      </div>
    );
  }

  return (
    <div className="onboard-step-content">
      <h2 className="onboard-step-title">W-9 Tax Form</h2>
      <p className="onboard-step-desc">
        As an independent contractor, we need your W-9 information for tax reporting purposes.
        This is required before your first commission payment.
      </p>

      <div className="onboard-w9-notice">
        <AlertIcon />
        Your SSN or EIN is encrypted and only used for IRS 1099 reporting.
      </div>

      <form onSubmit={handleSign} className="settings-form">
        <div className="settings-grid-2">
          <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
            <span>Full Legal Name</span>
            <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane A. Smith" />
          </label>
          <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
            <span>Social Security Number or EIN</span>
            <input
              required
              value={form.ssn}
              onChange={e => setForm(f => ({ ...f, ssn: e.target.value }))}
              placeholder="XXX-XX-XXXX"
              maxLength={11}
            />
          </label>
          <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
            <span>Street Address</span>
            <input required value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Main St" />
          </label>
          <label className="settings-field">
            <span>City</span>
            <input required value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Miami" />
          </label>
          <label className="settings-field">
            <span>State</span>
            <input required value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="FL" maxLength={2} />
          </label>
          <label className="settings-field">
            <span>ZIP Code</span>
            <input required value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} placeholder="33010" maxLength={10} />
          </label>
        </div>

        <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
          Under penalties of perjury, I certify that the information provided is correct.
        </p>
        <button
          type="submit"
          className="settings-btn-primary"
          disabled={signing || !form.name || !form.ssn || !form.address}
        >
          {signing ? "Submitting…" : "Submit W-9 →"}
        </button>
      </form>
    </div>
  );
}

function AlertIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

function CompleteStep() {
  const navigate = useNavigate();
  return (
    <div className="onboard-step-content onboard-complete">
      <div className="onboard-complete-icon">
        <CheckCircle size={48} color="#10b981" />
      </div>
      <h2 className="onboard-step-title">You're all set! 🎉</h2>
      <p className="onboard-step-desc">
        Your onboarding is complete. Head to your dashboard to start working your pipeline.
      </p>
      <div className="onboard-complete-actions">
        <button className="settings-btn-primary" onClick={() => navigate("/")}>
          Go to Dashboard <ArrowRight size={15} />
        </button>
        <button className="settings-btn-secondary" onClick={() => navigate("/leads")}>
          View My Leads
        </button>
      </div>
    </div>
  );
}

// ─── Main Onboarding Shell ────────────────────────────────────────────────────

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [signed, setSigned] = useState({
    contract: user?.onboarding?.contract_signed ?? false,
    tax_form: user?.onboarding?.tax_form_signed ?? false,
  });

  // Redirect admins
  useEffect(() => {
    if (user?.role === "admin") navigate("/");
  }, [user]);

  function handleSigned(doc) {
    setSigned(s => ({ ...s, [doc]: true }));
  }

  function canAdvance() {
    if (step === 3 && !signed.contract) return false;
    if (step === 4 && !signed.tax_form) return false;
    return true;
  }

  const currentStep = STEPS[step];

  return (
    <div className="onboard-shell">
      {/* Progress sidebar */}
      <aside className="onboard-progress">
        <div className="onboard-progress-logo">
          <img src="/Logo.png" alt="Logo" style={{ width: 28, height: 28 }} />
          <span>SalesCRM</span>
        </div>
        <p className="onboard-progress-label">Getting started</p>
        <div className="onboard-steps-list">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.key} className={`onboard-step-item ${done ? "done" : ""} ${active ? "active" : ""}`}>
                <div className="onboard-step-icon-wrap">
                  {done ? <Check size={14} /> : <s.icon size={14} />}
                </div>
                <span>{s.label}</span>
                {i < STEPS.length - 1 && <div className="onboard-step-connector" />}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Content */}
      <div className="onboard-content">
        <div className="onboard-card">
          {step === 0 && <WelcomeStep user={user} />}
          {step === 1 && <CompanyStep />}
          {step === 2 && <ProductsStep />}
          {step === 3 && <ContractStep onSigned={handleSigned} signed={signed.contract} />}
          {step === 4 && <TaxStep onSigned={handleSigned} signed={signed.tax_form} />}
          {step === 5 && <CompleteStep />}

          {step < STEPS.length - 1 && (
            <div className="onboard-nav">
              {step > 0 && (
                <button className="settings-btn-secondary onboard-back" onClick={() => setStep(s => s - 1)}>
                  <ChevronLeft size={15} /> Back
                </button>
              )}
              <button
                className="settings-btn-primary onboard-next"
                disabled={!canAdvance()}
                onClick={() => setStep(s => s + 1)}
              >
                {step === STEPS.length - 2 ? "Finish" : "Continue"}
                <ChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
